/**
 * Integrytas Group — AddressEditor (common).
 *
 * Reusable editor for the Standard Party Record's structured, multi-typed addresses
 * (CLAUDE.md §17.B8): a party holds MULTIPLE addresses (registered / billing /
 * delivery / pickup), each broken into line1/line2/city/county_state/postcode/country
 * — never a single free-text line. Drop into a party's add/edit modal; the parent
 * persists each row into the `<prefix>_<party>_address` table (see
 * scripts/templates/party_tables.sql.tmpl).
 *
 * Controlled: pass the current addresses + onChange.
 */

import { useTranslation } from 'react-i18next';

export const ADDRESS_TYPES = ['REGISTERED', 'BILLING', 'DELIVERY', 'PICKUP'] as const;

export interface PartyAddress {
  address_type: string;
  line1: string;
  line2?: string;
  city: string;
  county_state?: string;
  postcode?: string;
  country?: string;
}

export interface AddressEditorProps {
  value: PartyAddress[];
  onChange: (rows: PartyAddress[]) => void;
  /** Allowed address types; defaults to ADDRESS_TYPES. */
  types?: readonly string[];
  label?: string;
}

const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

export function AddressEditor({ value, onChange, types = ADDRESS_TYPES, label }: AddressEditorProps) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('common.address.label', 'Addresses');
  const update = (i: number, patch: Partial<PartyAddress>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const add = () =>
    onChange([...value, { address_type: types[0] ?? 'REGISTERED', line1: '', line2: '', city: '', county_state: '', postcode: '', country: '' }]);

  const input = 'h-7 w-full rounded border border-csm-border px-2 text-csm-sm';
  // Address lines wrap instead of scrolling/truncating in a fixed-height single
  // line (CLAUDE.md §11) — a full street address routinely exceeds one line.
  const lineInput = 'w-full resize-y rounded border border-csm-border px-2 py-1 text-csm-sm';

  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-csm-sm font-medium text-csm-ink-soft">{resolvedLabel}</span>
        <button type="button" onClick={add}
          className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">
          {t('common.address.add', '+ Add address')}
        </button>
      </div>
      {value.length === 0 && (
        <div className="px-2 py-2 text-csm-xs italic text-csm-watermark">{t('common.address.empty', 'No addresses — add at least one (registered).')}</div>
      )}
      <div className="flex flex-col gap-2">
        {value.map((a, i) => (
          <div key={i} className="rounded border border-csm-border bg-csm-surface-alt/40 p-2">
            <div className="mb-1 flex items-center justify-between">
              <select value={a.address_type} onChange={(e) => update(i, { address_type: e.target.value })}
                className="h-7 w-40 rounded border border-csm-border px-1 text-csm-sm">
                {types.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
              </select>
              <button type="button" onClick={() => remove(i)} className="text-csm-xs text-csm-danger" title={t('common.remove', 'Remove')}>×</button>
            </div>
            <div className="grid grid-cols-2 gap-1">
              <textarea className={`${lineInput} col-span-2`} rows={2} placeholder={t('common.address.line1', 'Address line 1')} value={a.line1} onChange={(e) => update(i, { line1: e.target.value })} />
              <textarea className={`${lineInput} col-span-2`} rows={2} placeholder={t('common.address.line2', 'Address line 2')} value={a.line2 ?? ''} onChange={(e) => update(i, { line2: e.target.value })} />
              <input className={input} placeholder={t('common.address.city', 'City')} value={a.city} onChange={(e) => update(i, { city: e.target.value })} />
              <input className={input} placeholder={t('common.address.county_state', 'County / State')} value={a.county_state ?? ''} onChange={(e) => update(i, { county_state: e.target.value })} />
              <input className={input} placeholder={t('common.address.postcode', 'Postcode')} value={a.postcode ?? ''} onChange={(e) => update(i, { postcode: e.target.value })} />
              <input className={input} placeholder={t('common.address.country', 'Country')} value={a.country ?? ''} onChange={(e) => update(i, { country: e.target.value })} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
